import { useEffect, useState } from "react";
import type { FieldDef } from "#shared/schemas/fieldDef.ts";
import type { FieldValueData } from "#shared/schemas/fieldValue.ts";
import { Checkbox } from "@web/components/ui/checkbox.tsx";
import { cn } from "@web/lib/utils.ts";
import { CountryCombobox } from "./CountryCombobox.tsx";

const INPUT =
  "h-8 w-full min-w-0 rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none";

function asString(value: FieldValueData | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}

function asList(value: FieldValueData | undefined): string[] {
  return Array.isArray(value) ? value : [];
}

/**
 * One editor per field type, driven entirely by the field definition — adding a
 * definition makes it editable here with no code change.
 *
 * Typed values commit on blur or Enter so a long note is one write, not one per
 * keystroke. Discrete values commit on change.
 */
export function FieldEditor({
  def,
  value,
  disabled,
  onCommit,
}: {
  def: FieldDef;
  value: FieldValueData | undefined;
  disabled: boolean;
  onCommit: (next: FieldValueData) => void;
}) {
  const [draft, setDraft] = useState(() => asString(value));

  // The cache is the source of truth; a refetch or an undo must win over a stale
  // draft, but not while the user is mid-edit in this input.
  useEffect(() => {
    setDraft(asString(value));
  }, [value]);

  const options = def.config?.options ?? [];
  const placeholder = def.config?.placeholder;

  function commitText(raw: string) {
    const trimmed = raw.trim();
    if (trimmed === asString(value).trim()) return;
    onCommit(trimmed === "" ? null : trimmed);
  }

  function commitNumber(raw: string) {
    const trimmed = raw.trim();
    if (trimmed === "") {
      if (value !== null && value !== undefined) onCommit(null);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed === value) return;
    onCommit(parsed);
  }

  switch (def.type) {
    case "longtext":
      return (
        <textarea
          value={draft}
          rows={def.config?.rows ?? 4}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={def.label}
          className={cn(
            INPUT,
            "h-auto resize-y py-1.5 leading-relaxed disabled:opacity-50",
          )}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setDraft(asString(value));
          }}
        />
      );

    case "number":
      return (
        <input
          type="number"
          value={draft}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={def.label}
          min={def.config?.min}
          max={def.config?.max}
          className={cn(INPUT, "disabled:opacity-50")}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitNumber(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitNumber(draft);
            if (e.key === "Escape") setDraft(asString(value));
          }}
        />
      );

    case "date":
      return (
        <input
          type="date"
          value={draft}
          disabled={disabled}
          aria-label={def.label}
          className={cn(INPUT, "disabled:opacity-50")}
          onChange={(e) => {
            setDraft(e.target.value);
            onCommit(e.target.value === "" ? null : e.target.value);
          }}
        />
      );

    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-sm text-muted">
          <Checkbox
            checked={value === true}
            disabled={disabled}
            aria-label={def.label}
            onCheckedChange={(checked) => onCommit(checked === true)}
          />
          {value === true ? "Yes" : "No"}
        </label>
      );

    case "select":
      return (
        <select
          value={asString(value)}
          disabled={disabled}
          aria-label={def.label}
          className={cn(INPUT, "disabled:opacity-50")}
          onChange={(e) => onCommit(e.target.value === "" ? null : e.target.value)}
        >
          <option value="">—</option>
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case "country":
      return (
        <CountryCombobox
          value={asString(value) || undefined}
          disabled={disabled}
          aria-label={def.label}
          onCommit={onCommit}
        />
      );

    case "multiselect": {
      const selected = asList(value);
      if (options.length === 0) {
        return <p className="text-xs text-faint">This field has no options configured yet.</p>;
      }
      return (
        <div className="flex flex-col gap-1.5">
          {options.map((opt) => {
            const checked = selected.includes(opt.id);
            return (
              <label key={opt.id} className="flex items-center gap-2 text-sm text-muted">
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  aria-label={opt.label}
                  onCheckedChange={(next) => {
                    const list = next === true
                      ? [...selected, opt.id]
                      : selected.filter((id) => id !== opt.id);
                    onCommit(list.length === 0 ? null : list);
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }

    case "url":
    case "text":
      return (
        <input
          type={def.type === "url" ? "url" : "text"}
          value={draft}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={def.label}
          className={cn(INPUT, "disabled:opacity-50")}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitText(draft);
            if (e.key === "Escape") setDraft(asString(value));
          }}
        />
      );
  }
}
